import { Box, Skeleton } from '@mui/material';
import { useLayoutEffect, useMemo } from 'react';
import { FlowResourceType, ISwitcherButton, Switcher, useEditorStore } from '@diana/core';
import { useTranslation } from 'react-i18next';
import {
  BuilderToolsDrawerTabValue,
  BuilderTypeEnum,
} from '../../Store/FlowBuilder2PageStore.types';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import styled from 'styled-components';

export const TOOLS_DRAWER_HEADER_HEIGHT = 52;

export const ToolsDrawerHeaderWrapper = styled(Box)(() => ({
  paddingBottom: '4px',
  paddingTop: '12px',
  paddingLeft: '12px',
  paddingRight: '12px',
  height: TOOLS_DRAWER_HEADER_HEIGHT,
}));

export const ToolsDrawerHeader = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { isLoading: isPageLoading } = useFlowBuilder2PageContext();

  const activeTab = useFlowBuilder2PageStore((s) => s.leftDrawerActiveTab);
  const setActiveTab = useFlowBuilder2PageStore((s) => s.setLeftDrawerActiveTab);
  const builderType = useFlowBuilder2PageStore((s) => s.builderType);
  const setBuilderType = useFlowBuilder2PageStore((s) => s.setBuilderType);

  const isAssetType = useEditorStore(
    (store) => store.flow?.properties.flowResourceType === FlowResourceType.Entity,
  );
  const setEnableAeAutoSelect = useEditorStore((store) => store.setEnableAeAutoSelect);

  const tabs = useMemo<ISwitcherButton[]>(() => {
    const result: ISwitcherButton[] = [];
    result.push({
      text: isAssetType ? t('labels.asset') : t('labels.workflow'),
      name: BuilderToolsDrawerTabValue.Explorer,
    });
    result.push({ text: t('labels.rules'), name: BuilderToolsDrawerTabValue.Rules });
    result.push({ text: t('labels.properties'), name: BuilderToolsDrawerTabValue.Properties });
    return result;
  }, [t, isAssetType]);

  const handleClick = (tab: BuilderToolsDrawerTabValue) => {
    if (tab === BuilderToolsDrawerTabValue.Rules || tab === BuilderToolsDrawerTabValue.Properties) {
      setBuilderType(BuilderTypeEnum.AdvancedEditor);
    }
    setEnableAeAutoSelect(true);
  };

  useLayoutEffect(() => {
    if (tabs.length > 0 && builderType === BuilderTypeEnum.Nocode) {
      setActiveTab(tabs[0].name as BuilderToolsDrawerTabValue);
    }
  }, [tabs, setActiveTab, builderType]);

  if (isPageLoading)
    return (
      <ToolsDrawerHeaderWrapper>
        <Skeleton variant="rounded" width={235} height={36} sx={{ px: 1.5, py: 1.5, pb: 0.5 }} />
      </ToolsDrawerHeaderWrapper>
    );

  if (tabs.length === 0) return null;

  return (
    <ToolsDrawerHeaderWrapper>
      <Switcher
        buttons={tabs}
        value={activeTab}
        setValue={setActiveTab}
        onClick={(tab) => handleClick(tab as BuilderToolsDrawerTabValue)}
      />
    </ToolsDrawerHeaderWrapper>
  );
};
